import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { ScrollView, View } from 'react-native'

import { Placeholder } from '@/src/elements/placeholder'
import { EVENT_ORIGIN } from '@/src/features/community/models/community.constants'
import { useDoneCommunitiesQuery } from '@/src/features/community/services/useDoneCommunitiesQuery'
import { CommunityCard } from '@/src/features/community/ui/components/CommunityCard'
import { EmptyState } from '@/src/general/components/EmptyState'
import { ErrorMessage } from '@/src/general/components/ErrorMessage'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useDimensions } from '@/src/general/hooks/useDimensions'
import { TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'

export const CompletedSubscriptionsScreen = (): ReactNode => {
  const { lessThan } = useDimensions()
  const { data: doneCommunities, error } = useDoneCommunitiesQuery()

  const hasDoneCommunities = doneCommunities && doneCommunities.length > 0

  if (error) return <ErrorMessage error={error} defaultErrorKey="community.load.error" />

  if (!doneCommunities) return <Placeholder className="h-full" testID={TEST_ID.PROFILE.DONE.LOADING} />

  if (doneCommunities.length === 0)
    return (
      <EmptyState icon={ICON.HOURGLASS_HALF} title={<FormattedMessage id="community.communities.none-finished" />} />
    )

  return (
    <ScrollView
      testID={TEST_ID.PROFILE.COMPLETED_SUBSCRIPTIONS_SCREEN}
      showsVerticalScrollIndicator={!lessThan.md}
      contentContainerClassName="mx-auto w-full pt-4 pb-32 md:max-w-screen-md"
    >
      {hasDoneCommunities && (
        <View className="flex w-full flex-row flex-wrap justify-center gap-5 md:justify-start px-4">
          {doneCommunities.map((community) => (
            <CommunityCard
              key={community.id}
              testId={TEST_ID.PROFILE.DONE.COMMUNITY_CARDS}
              community={community}
              source={TRACKING_SOURCE.PROFILE_PAGE}
              origin={EVENT_ORIGIN.PROFILE_ANIMATOR}
              showSubscribeButton={false}
              fullWidth
            />
          ))}
        </View>
      )}
    </ScrollView>
  )
}
